/**
 * 委托书自身的「编制流程」分节。
 *
 * 重要区分：这与主流程的 19 步是**两个层级**。
 *   · 主流程第 5 步「委托书创建」= 整个委托书页面（也就是这里的 8 节全部）
 *   · 主流程第 6 步「委托书审批」= 独立的审批画面
 *   · 主流程第 7~13 步 = 另外的画面，不在委托书详情页里
 *
 * 分节取自参考系统 drap-dev 的 DESIGN SHEET PROCESS 01~08。
 */
import { CHARGE_ROLE } from './codes.js';
import { PERMISSION } from './permissions.js';
export const SHEET_SECTION = {
    /** 01 设计：图示图稿 / 主信息 / 详细图片 */
    DESIGN: 'DESIGN',
    /** 02 原辅材料：主材料·ColorWay / 辅材料 BOM */
    MATERIAL: 'MATERIAL',
    /** 03 版型·尺寸：基准尺寸（样品标准说明） */
    PATTERN: 'PATTERN',
    /** 04 订货数量：上游确定后保存的只读快照 */
    QUANTITY: 'QUANTITY',
    /** 05 检讨·Tech Pack */
    TECHPACK: 'TECHPACK',
    /** 06 QC 委托书 */
    QC: 'QC',
    /** 07 在线商品信息 */
    ONLINE: 'ONLINE',
    /** 08 USP·设计意图 */
    USP: 'USP',
};
/**
 * 分节的完成标记项。
 *
 * 原辅材料与版型两节由不同角色分别打标记（参考系统截图里
 * 「디자이너 작성 완료 / 생산 검토 완료 / 요척 입력 완료」就是三个独立标记），
 * 其余节只有单一的「本节完成」。
 *
 * 各标记对应主流程的哪一步、由谁打，取自旧 design sheet 的流程字段表：
 *
 * | 标记 | 主流程步骤 | 责任角色 |
 * |---|---|---|
 * | DESIGNER_DONE       | 5 委托书创建            | 设计师 |
 * | PURCHASE_DONE       | 面辅料采购资料核对      | 面辅料采购（报价缺项不阻拦） |
 * | PRODUCTION_REVIEWED | 7 面料幅宽 · 8 辅料信息  | 购买/生产、Pattern 担当 |
 * | USAGE_ENTERED       | 10 原材料用量 · 11 辅材料用量 | CAD 担当 |
 * | BASE_SIZE_DONE      | 9 基准尺寸              | Pattern 担当 |
 * | SIZE_SPEC_DONE      | 12 尺寸偏差             | CAD 担当 |
 *
 * 第 8 步由 Pattern 的 TRIM_TECHNICAL_DONE 单独留痕，与 CAD 用量确认分开。
 */
export const STAGE = {
    /** 通用：本节完成 */
    STEP_DONE: 'STEP_DONE',
    /** 原辅材料：设计录入完成（第 5 步 · 设计师） */
    DESIGNER_DONE: 'DESIGNER_DONE',
    /**
     * 原辅材料：面辅料采购完成 —— 面料定了、面料和辅料的单价都录了（面辅料采购）。
     * 只设一个标记（2026-09-25）：别人等采购，等的是「成本现在能信了吗」这一句话，
     * 拆成面料 / 单价 / 辅料单价只会多出没人看的中间状态。
     */
    PURCHASE_DONE: 'PURCHASE_DONE',
    /** 原辅材料：生产检讨完成（第 7、8 步 · 购买/生产、Pattern 担当） */
    PRODUCTION_REVIEWED: 'PRODUCTION_REVIEWED',
    /** 第 8 步：Pattern 单独确认辅料技术资料。 */
    TRIM_TECHNICAL_DONE: 'TRIM_TECHNICAL_DONE',
    /** 原辅材料：用量录入完成（第 10、11 步 · CAD 担当） */
    USAGE_ENTERED: 'USAGE_ENTERED',
    /** 版型：基准尺寸录入完成（第 9 步 · Pattern 担当） */
    BASE_SIZE_DONE: 'BASE_SIZE_DONE',
    /** 版型：尺寸规格录入完成（第 12 步 · CAD 担当） */
    SIZE_SPEC_DONE: 'SIZE_SPEC_DONE',
    /** @deprecated 历史数量确认记录；2026-10-03 起不再要求或允许重复确认。 */
    QUANTITY_CONFIRMED: 'QUANTITY_CONFIRMED',
};
export const SHEET_SECTIONS = [
    {
        section: SHEET_SECTION.DESIGN,
        no: 1,
        tabs: ['images', 'main'],
        stages: [STAGE.STEP_DONE],
        implemented: true,
        enabled: true,
    },
    {
        section: SHEET_SECTION.MATERIAL,
        no: 2,
        tabs: ['fabric', 'trimBom', 'cost'],
        // 仅为显示顺序，各岗位可并行录入和确认。
        stages: [STAGE.DESIGNER_DONE, STAGE.PURCHASE_DONE, STAGE.PRODUCTION_REVIEWED, STAGE.TRIM_TECHNICAL_DONE, STAGE.USAGE_ENTERED],
        implemented: true,
        enabled: true,
    },
    {
        section: SHEET_SECTION.PATTERN,
        no: 3,
        tabs: ['size', 'deviation'],
        stages: [STAGE.BASE_SIZE_DONE, STAGE.SIZE_SPEC_DONE],
        implemented: true,
        enabled: true,
    },
    {
        section: SHEET_SECTION.QUANTITY,
        no: 4,
        tabs: ['quantity'],
        stages: [],
        implemented: true,
        enabled: true,
    },
    {
        section: SHEET_SECTION.TECHPACK,
        no: 5,
        tabs: ['techpack'],
        stages: [STAGE.STEP_DONE],
        implemented: true,
        enabled: true,
    },
    { section: SHEET_SECTION.QC, no: 6, tabs: ['qc'], stages: [STAGE.STEP_DONE], implemented: true, enabled: true },
    // 后续阶段启用；灰度关闭时，前后端都以 enabled 为准，不删除存量资料。
    { section: SHEET_SECTION.ONLINE, no: 7, tabs: ['online'], stages: [STAGE.STEP_DONE], implemented: true, enabled: false },
    { section: SHEET_SECTION.USP, no: 8, tabs: ['usp'], stages: [STAGE.STEP_DONE], implemented: true, enabled: false },
];
/**
 * 打某个分节完成标记时，回写哪些担当的确认日期。
 *
 * 对应旧 design sheet 的「XX担当确认后的委托书」—— 打标记这个动作本身就是"确认"，
 * Tech Pack 页眉担当那一排的日期就是这么来的，不需要再让人单独填一次日期。
 */
export function chargeRolesOfStage(section, stage) {
    if (section === SHEET_SECTION.DESIGN && stage === STAGE.STEP_DONE)
        return [CHARGE_ROLE.DESIGN];
    if (section === SHEET_SECTION.MATERIAL) {
        if (stage === STAGE.DESIGNER_DONE)
            return [CHARGE_ROLE.DESIGN];
        // 页眉的原材料、辅料担当确认日归面辅料采购的标记（09-25 之前挂在生产检讨完成上）
        if (stage === STAGE.PURCHASE_DONE)
            return [CHARGE_ROLE.FABRIC, CHARGE_ROLE.TRIM];
        if (stage === STAGE.PRODUCTION_REVIEWED)
            return [CHARGE_ROLE.PRODUCTION];
        if (stage === STAGE.TRIM_TECHNICAL_DONE)
            return [CHARGE_ROLE.PATTERN];
        if (stage === STAGE.USAGE_ENTERED)
            return [CHARGE_ROLE.CAD];
    }
    if (section === SHEET_SECTION.PATTERN) {
        if (stage === STAGE.BASE_SIZE_DONE)
            return [CHARGE_ROLE.PATTERN];
        if (stage === STAGE.SIZE_SPEC_DONE)
            return [CHARGE_ROLE.CAD];
    }
    if (section === SHEET_SECTION.TECHPACK)
        return [CHARGE_ROLE.TECHPACK];
    if (section === SHEET_SECTION.ONLINE)
        return [CHARGE_ROLE.PLAN];
    if (section === SHEET_SECTION.USP)
        return [CHARGE_ROLE.DESIGN];
    // 06 QC 刻意返回空：QC 担当是**按轮次**的（SP0310 的 QCNO 就在明细行上），
    // 一份委托书可能有多轮、由不同人做，塞不进 ds_charge 的「一角色一行」。
    // QC 担当落在 QC 轮次自己的表上，页眉担当那一排不加第 9 列 —— 那一排的列数与打印件绑定。
    return [];
}
export function getSectionDef(section) {
    return SHEET_SECTIONS.find((s) => s.section === section);
}
/** 本版已实现的分节 */
export const IMPLEMENTED_SECTIONS = SHEET_SECTIONS.filter((s) => s.implemented);
/** 本版实际开放的分节：导航、进度、提交检查共用这一份清单。 */
export const ENABLED_SECTIONS = IMPLEMENTED_SECTIONS.filter((s) => s.enabled);
export function isSheetSectionEnabled(section) {
    return ENABLED_SECTIONS.some((s) => s.section === section);
}
/** 旧收藏链接指向未开放的节时，返回设计节，避免绕过导航显示灰度内容。 */
export function resolveSheetSection(section) {
    return ENABLED_SECTIONS.find((s) => s.section === section)?.section ?? SHEET_SECTION.DESIGN;
}
export function sheetStageProgress(stages) {
    let done = 0;
    let total = 0;
    for (const def of ENABLED_SECTIONS) {
        for (const stage of def.stages) {
            total++;
            if (stages.some((s) => s.section === def.section && s.stage === stage && s.completed))
                done++;
        }
    }
    return { done, total };
}
/**
 * 发生在委托书审批**之后**的分节 —— 目前只有 06 QC（样品做出来才有 QC）。
 *
 * 这类分节和其它节有三处规则不同，前后端都从这里取，不要各处各写一遍 `=== SHEET_SECTION.QC`：
 *   · 不进提交预检 —— 它的「本节完成」表示 QC 环节整体结束，不是提交的前提
 *   · 写入不受委托书状态机约束，只挡 CANCELLED（后端 `assertQcWritable`）
 *   · 权限点单独一套（见 `editPermissionOf` / `stagePermissionOf`）
 */
export function isPostApprovalSection(section) {
    return section === SHEET_SECTION.QC;
}
/**
 * 写本节内容（含本节的附件）需要的权限点。
 * QC 的录入者是生产跟单，不是委托书的编制者，所以不能用 sheet:update。
 */
export function editPermissionOf(section) {
    return section === SHEET_SECTION.QC ? PERMISSION.QC_EDIT : PERMISSION.SHEET_UPDATE;
}
/**
 * 打本节完成标记需要的权限点。
 * QC 的「本节完成」是宣布 QC 环节整体结束，和「轮次完成」同属确认性质的动作，用 qc:confirm。
 */
export function stagePermissionOf(section) {
    return section === SHEET_SECTION.QC ? PERMISSION.QC_CONFIRM : PERMISSION.SHEET_UPDATE;
}
/**
 * 附件用途。
 * 取自参考系统（도식화 / 상세이미지 / FG이미지 / 패턴스펙이미지 / 미니마카）
 * 与旧系统（辅材图片 / 标签缝制方法 / Shipping 信息）。
 */
export const ATTACHMENT_KIND = {
    /** 图示图稿（도식화）—— 委托书的核心图，一张 */
    FLAT_SKETCH: 'FLAT_SKETCH',
    /** 详细图片（상세이미지）—— 多张 */
    DETAIL_IMAGE: 'DETAIL_IMAGE',
    /** 成品实物图（FG이미지） */
    FG_IMAGE: 'FG_IMAGE',
    /**
     * 设计原稿：设计师上传的整份 PDF / PPT 原件。拆页导入时顺带存下，
     * 留给版式识别（P9 D5）读文字层 / 表格结构，也便于追溯。不进 Tech Pack，不参与预填。
     */
    DESIGN_SOURCE: 'DESIGN_SOURCE',
    /** 辅材图片 */
    SUB_MATERIAL_IMAGE: 'SUB_MATERIAL_IMAGE',
    /** 标签缝制方法 */
    LABEL_SEWING: 'LABEL_SEWING',
    /** Shipping 信息（旧的单一用途，保留以兼容已有数据；新数据用下面四个分区） */
    SHIPPING: 'SHIPPING',
    /** Shipping 第 8 页：吊牌 TAGGING */
    SHIP_TAGGING: 'SHIP_TAGGING',
    /** Shipping 第 8 页：装箱 BOXING */
    SHIP_BOXING: 'SHIP_BOXING',
    /** Shipping 第 8 页：折叠与包装 FOLDING & PACKING */
    SHIP_FOLDING: 'SHIP_FOLDING',
    /** Shipping 第 8 页：熨烫 IRON */
    SHIP_IRON: 'SHIP_IRON',
    /** 版型规格图（패턴스펙이미지） */
    PATTERN_SPEC_IMAGE: 'PATTERN_SPEC_IMAGE',
    /**
     * QC 附件（工厂回传的 Excel、样品照片）。按轮次挂，refSeq = 该轮的 qcSeq。
     * 本期只存文件不解析 —— 工厂用的 Excel 模板还没定，见 docs/step6-8-plan.md 3.4。
     */
    QC_ATTACHMENT: 'QC_ATTACHMENT',
};
/**
 * 单文件用途（替换而非追加），其余为多文件。
 *
 * 辅材图片是「每行只留一张」，它的替换语义原先是靠 ROW_SCOPED_KINDS 顺带实现的 ——
 * 那是把「按 refSeq 分组」和「只留一张」两件事混在了一起。
 * QC 附件同样按 refSeq 分组，但一轮 QC 要挂多个文件（Excel + 样品照片），
 * 所以这里把辅材图片显式列进来，ROW_SCOPED_KINDS 只负责分组。
 * 版型资料允许图片、PDF 和 CAD 原件并存；替换只针对用户选中的文件。
 */
export const SINGLE_FILE_KINDS = [
    ATTACHMENT_KIND.SUB_MATERIAL_IMAGE,
    ATTACHMENT_KIND.FLAT_SKETCH,
    ATTACHMENT_KIND.SHIP_TAGGING,
    ATTACHMENT_KIND.SHIP_BOXING,
    ATTACHMENT_KIND.SHIP_FOLDING,
    ATTACHMENT_KIND.SHIP_IRON,
];
/**
 * 按 refSeq 分组的附件用途。上传时必须带 refSeq，「单文件」语义也随之变成「该组只保留一张」。
 *
 * 这里**只决定分组**，一组能放几个由 SINGLE_FILE_KINDS 决定：
 *   · 辅材图片 —— refSeq = 辅料明细行的 seq，每行一张（Tech Pack 第 6 页）
 *   · QC 附件 —— refSeq = ds_qc_round.qc_seq，一轮可以多个
 */
export const ROW_SCOPED_KINDS = [
    ATTACHMENT_KIND.SUB_MATERIAL_IMAGE,
    // QC 附件的 refSeq 指的是 ds_qc_round.qc_seq，不是明细行序号 —— 语义相同：挂在某一条记录上
    ATTACHMENT_KIND.QC_ATTACHMENT,
];
/** Shipping Instruction 第 8 页的四个分区，按打印件顺序 */
export const SHIPPING_KINDS = [
    ATTACHMENT_KIND.SHIP_TAGGING,
    ATTACHMENT_KIND.SHIP_BOXING,
    ATTACHMENT_KIND.SHIP_FOLDING,
    ATTACHMENT_KIND.SHIP_IRON,
];
/** 各附件用途归属哪一节，决定它在界面哪里出现 */
export const ATTACHMENT_SECTION = {
    [ATTACHMENT_KIND.FLAT_SKETCH]: SHEET_SECTION.DESIGN,
    [ATTACHMENT_KIND.DETAIL_IMAGE]: SHEET_SECTION.DESIGN,
    [ATTACHMENT_KIND.FG_IMAGE]: SHEET_SECTION.DESIGN,
    [ATTACHMENT_KIND.DESIGN_SOURCE]: SHEET_SECTION.DESIGN,
    [ATTACHMENT_KIND.SUB_MATERIAL_IMAGE]: SHEET_SECTION.MATERIAL,
    [ATTACHMENT_KIND.LABEL_SEWING]: SHEET_SECTION.MATERIAL,
    [ATTACHMENT_KIND.SHIPPING]: SHEET_SECTION.MATERIAL,
    [ATTACHMENT_KIND.SHIP_TAGGING]: SHEET_SECTION.MATERIAL,
    [ATTACHMENT_KIND.SHIP_BOXING]: SHEET_SECTION.MATERIAL,
    [ATTACHMENT_KIND.SHIP_FOLDING]: SHEET_SECTION.MATERIAL,
    [ATTACHMENT_KIND.SHIP_IRON]: SHEET_SECTION.MATERIAL,
    [ATTACHMENT_KIND.PATTERN_SPEC_IMAGE]: SHEET_SECTION.PATTERN,
    [ATTACHMENT_KIND.QC_ATTACHMENT]: SHEET_SECTION.QC,
};
/** 允许上传的图片类型与大小上限 */
export const ATTACHMENT_ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'application/pdf'];
export const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const PPT_MIME = 'application/vnd.ms-powerpoint';
/** 设计原稿收 PDF 与 PPT。样本里最大的 PPT 有 56 MB（多轮修改累积在一个文件里），上限放到 100 MB。 */
export const DESIGN_SOURCE_ACCEPT = ['application/pdf', PPTX_MIME, PPT_MIME];
export const DESIGN_SOURCE_MAX_BYTES = 100 * 1024 * 1024;
/** Illustrator 原件只下载，不作为浏览器图片或 PDF 解析。 */
export const ILLUSTRATOR_MIME = 'application/postscript';
export const FG_ACCEPT = [...ATTACHMENT_ACCEPT, ILLUSTRATOR_MIME];
/** CAD originals are downloads, not browser images, even when a browser reports image/vnd.dwg. */
export const CAD_FILE_MIMES = { dxf: 'application/dxf', dwg: 'application/acad', plt: 'application/vnd.hp-hpgl' };
export const PATTERN_ACCEPT = [...ATTACHMENT_ACCEPT, ILLUSTRATOR_MIME, ...Object.values(CAD_FILE_MIMES)];
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLS_MIME = 'application/vnd.ms-excel';
/** QC 附件额外收 Excel —— 工厂回传的检验表就是 Excel（2026-10-02 试点前确认） */
export const QC_EXTRA_ACCEPT = [XLSX_MIME, XLS_MIME];
export function attachmentAcceptOf(kind) {
    if (kind === ATTACHMENT_KIND.DESIGN_SOURCE)
        return DESIGN_SOURCE_ACCEPT;
    if (kind === ATTACHMENT_KIND.FG_IMAGE)
        return FG_ACCEPT;
    if (kind === ATTACHMENT_KIND.PATTERN_SPEC_IMAGE)
        return PATTERN_ACCEPT;
    return kind === ATTACHMENT_KIND.QC_ATTACHMENT ? [...ATTACHMENT_ACCEPT, ...QC_EXTRA_ACCEPT] : ATTACHMENT_ACCEPT;
}
export function attachmentMaxBytesOf(kind) {
    return kind === ATTACHMENT_KIND.DESIGN_SOURCE || kind === ATTACHMENT_KIND.PATTERN_SPEC_IMAGE ? DESIGN_SOURCE_MAX_BYTES : ATTACHMENT_MAX_BYTES;
}
/**
 * 文件的 MIME 类型。没装 Office 的 Windows 上，浏览器给 .pptx 报的是空串或 octet-stream，
 * 这时按扩展名补上 —— 否则 PPT 原稿永远传不上去。
 */
export function attachmentMimeOf(file) {
    // AI 可能被浏览器识别成 PDF；按扩展名保留原件用途，避免打开或解析为 PDF。
    if (/\.ai$/i.test(file.name))
        return ILLUSTRATOR_MIME;
    const cadExt = file.name.toLowerCase().split('.').pop();
    if (cadExt && cadExt in CAD_FILE_MIMES)
        return CAD_FILE_MIMES[cadExt];
    if (file.type && file.type !== 'application/octet-stream')
        return file.type;
    const ext = file.name.toLowerCase().split('.').pop();
    if (ext === 'pptx')
        return PPTX_MIME;
    if (ext === 'ppt')
        return PPT_MIME;
    if (ext === 'pdf')
        return 'application/pdf';
    if (ext === 'xlsx')
        return XLSX_MIME;
    if (ext === 'xls')
        return XLS_MIME;
    return file.type;
}
//# sourceMappingURL=sheet-sections.js.map